import React from 'react';
import type { ReactNode } from 'react';
import { Check, AlertCircle, Loader2, ZoomIn, X } from 'lucide-react';
import AvatarDisplay from '../../../../components/feature/avatar/AvatarDisplay';
import type { AvatarSize } from '../../../../components/feature/avatar/AvatarDisplay';
import type { MatchedEntity } from '../../../../stores/registerStore';


/** 表单输入组件（注册步骤通用） */
export function FormInput({
  label,
  value,
  onChange,
  onBlur,
  error,
  placeholder,
  type = 'text',
  rightElement,
  required = false,
  validate,
  inputMode,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  placeholder: string;
  type?: string;
  rightElement?: ReactNode;
  required?: boolean;
  validate?: () => void;
  inputMode?: 'text' | 'numeric' | 'tel' | 'email';
  maxLength?: number;
}) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    if (validate) {
      validate();
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label}
        {required && <span className="text-red-400 text-xs ml-1">*</span>}
      </label>
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          inputMode={inputMode}
          maxLength={maxLength}
          className={`w-full px-4 py-3 border rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 transition-colors ${
            error ? 'border-red-300 focus:border-red-400 bg-red-50' : 'bg-slate-50 border-slate-200 focus:bg-white focus:border-teal-500'
          } ${rightElement ? 'pr-24' : ''}`}
          placeholder={placeholder}
        />
        {rightElement && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            {rightElement}
          </div>
        )}
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

/** 唯一性检查状态指示器 */
export function StatusIndicator({ status }: { status: 'idle' | 'checking' | 'ok' | 'taken' | 'customer' }) {
  if (status === 'checking') {
    return <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />;
  }
  if (status === 'ok') {
    return <Check className="w-5 h-5 text-green-500" />;
  }
  if (status === 'taken') {
    return <AlertCircle className="w-5 h-5 text-red-500" />;
  }
  if (status === 'customer') {
    return <AlertCircle className="w-5 h-5 text-amber-500" />;
  }
  return null;
}


/** 命中明细徽章去重：同一 label 只保留第一条（detail 放入 title 悬停查看） */
function dedupeBadges(fields: MatchedEntity['matchedFields']) {
  const seen = new Set<string>();
  return (fields ?? []).filter((mf) => {
    if (seen.has(mf.label)) return false;
    seen.add(mf.label);
    return true;
  });
}

/**
 * 命中明细徽章组：label 去重，detail 悬停查看。
 * variant 区分主档/认领卡片（teal）与次档紧凑卡片（slate，可截断条数）。
 */
export function MatchedFieldBadges({
  fields,
  max,
  variant = 'primary',
}: {
  fields?: MatchedEntity['matchedFields'];
  max?: number;
  variant?: 'primary' | 'secondary';
}) {
  const badges = dedupeBadges(fields);
  const shown = max != null ? badges.slice(0, max) : badges;
  if (shown.length === 0) return null;
  const badgeClass =
    variant === 'primary'
      ? 'px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full font-medium'
      : 'px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full';
  return (
    <>
      {shown.map((mf, i) => (
        <span key={i} title={mf.detail || undefined} className={badgeClass}>
          {mf.label}
        </span>
      ))}
    </>
  );
}

/**
 * 可点击放大的头像按钮：头像 + 悬停 ZoomIn 覆盖层，点击触发 onZoom。
 * scope 为 'avatar' 时使用命名组 group/avatar（用于嵌套在 group 卡片内的场景）。
 */
export function ZoomAvatarButton({
  name,
  avatarUrl,
  size = 'md',
  ring,
  scope,
  onZoom,
}: {
  name?: string | null;
  avatarUrl?: string | null;
  size?: AvatarSize;
  ring?: string;
  scope?: 'avatar';
  onZoom: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (avatarUrl) onZoom();
      }}
      className={`relative shrink-0 rounded-full ${
        avatarUrl ? (scope === 'avatar' ? 'cursor-zoom-in group/avatar' : 'cursor-zoom-in group') : 'cursor-default'
      }`}
      title={avatarUrl ? '点击放大查看照片' : undefined}
    >
      <AvatarDisplay name={name} avatarUrl={avatarUrl} size={size} ring={ring} />
      {avatarUrl && (
        <span
          className={`absolute inset-0 flex items-center justify-center rounded-full bg-black/0 transition-colors ${
            scope === 'avatar' ? 'group-hover/avatar:bg-black/30' : 'group-hover:bg-black/30'
          }`}
        >
          <ZoomIn
            className={`${scope === 'avatar' ? 'w-3.5 h-3.5' : 'w-5 h-5'} text-white opacity-0 transition-opacity ${
              scope === 'avatar' ? 'group-hover/avatar:opacity-100' : 'group-hover:opacity-100'
            }`}
          />
        </span>
      )}
    </button>
  );
}

/** 头像/照片全屏放大预览层（点击遮罩或关闭按钮关闭） */
export function ZoomPreviewLayer({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-6" onClick={onClose}>
      <div className="relative">
        <img src={src} alt={alt} className="max-w-[80vw] max-h-[80vh] rounded-xl shadow-2xl object-contain" />
        <button
          type="button"
          className="absolute -top-3 -right-3 w-9 h-9 bg-white rounded-full shadow-lg flex items-center justify-center text-slate-600 hover:text-slate-900"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="关闭照片预览"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}